import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import './FilterControllerModal.css';

import { Organization } from 'src/models/organization';
import { Firmware } from 'src/models/firmware';
import { FilterOptionState } from './MobileControllersListPage';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { close } from 'ionicons/icons';
import { getFirmwareDisplayFromFirmware } from '../../utils/helpers/firmwareHelpers';
import { InputSelect } from 'src/components/reusable-components/InputSelect';

interface FilterControllerProps {
  /**
   * The Organizations options for the controller filter.
   */
  organizations: Organization[];
  /**
   * The firmware version options for the controller filter.
   */
  firmwares: Firmware[];
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal and reset the state.
   */
  onClose: () => void;
  /**
   * Triggers the request to close the modal and save the state object.
   */
  onSave: (e: FilterOptionState) => void;
}
export const FilterControllerModal: React.FC<FilterControllerProps> = ({
  organizations,
  firmwares,
  onClose,
  isOpen,
  onSave,
}) => {
  const [filterSelections, setFilterSelections] = useState<FilterOptionState>({
    firmware: undefined,
    organization: undefined,
  });
  const intl = useIntl();

  useEffect(() => {
    setFilterSelections({ firmware: undefined, organization: undefined });
  }, [isOpen]);

  // Sort by version number -> 1.2.0, 1.1.5, 1.1.0, 0.1.1, etc.
  firmwares.sort((a, b) => {
    return a?.version > b.version ? -1 : 1;
  });

  const organizationSelectOptions = organizations.map(org => {
    return {
      value: org._id,
      label: org.orgName,
    };
  }, []);

  const firmwareSelectOptions = firmwares.map(firmware => ({
    value: firmware._id,
    label: getFirmwareDisplayFromFirmware(intl, firmware),
  }));

  return (
    <IonModal className="controller-filter-modal" isOpen={isOpen} onDidDismiss={() => onClose()}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{intl.formatMessage(ControllerMessages.filter)}</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={() => onClose()}>
              <IonIcon size="large" color="dark" slot="icon-only" icon={close} onClick={() => onClose()} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <InputSelect<string>
          label={intl.formatMessage(ControllerMessages.organization)}
          options={organizationSelectOptions}
          value={filterSelections.organization}
          onChange={orgId => setFilterSelections({ ...filterSelections, organization: orgId })}
          placeholder={intl.formatMessage(ControllerMessages.organizationPlaceholder)}
          id="filter-controller-select-organization-input"
        />

        <InputSelect<string>
          label={intl.formatMessage(ControllerMessages.firmwareVersionLabel)}
          options={firmwareSelectOptions}
          value={filterSelections.firmware}
          onChange={firmware => setFilterSelections({ ...filterSelections, firmware: firmware })}
          placeholder={intl.formatMessage(ControllerMessages.selectFirmwareVersion)}
          id="filter-controller-select-firmware-input"
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton onClick={() => onSave(filterSelections)} fill="solid" expand="block" color="primary">
            {intl.formatMessage(ControllerMessages.filterControllers)}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default FilterControllerModal;
